import { useCallback, useState, useEffect } from 'react'
import { useNodeId, useStore } from '@xyflow/react'
import { useNodeAsset } from '@/entities/node/canvas-asset'
import { useCustomNodeData } from './useCustomNodeData'
import { useNodePreviewRequest, clearNodePreviewRequest } from '@/entities/node/model/node-preview-store'

const PANORAMA_OP = 'panorama'
const DIRECTOR_OP = 'director'

function hasPanoramaOp(snapshot: { op?: string } | null | undefined) {
  return snapshot?.op === PANORAMA_OP
}

function hasDirectorOp(snapshot: { op?: string } | null | undefined) {
  return snapshot?.op === DIRECTOR_OP
}

/**
 * 双击节点打开预览
 * 支持图片、视频、音频和文本节点的预览
 * 如果图片节点带 panorama OP，则打开 PanoramaViewer
 */
export function useNodeDoubleClickPreview(nodeId?: string | null) {
  const contextNodeId = useNodeId()
  const id = nodeId ?? contextNodeId
  const [previewOpen, setPreviewOpen] = useState(false)
  const [panoramaOpen, setPanoramaOpen] = useState(false)
  const [directorOpen, setDirectorOpen] = useState(false)

  // 获取节点数据和素材
  const { snapshot } = useCustomNodeData()
  const nodeAsset = useNodeAsset()

  // 从 React Flow store 获取节点类型
  const nodeType = useStore((s) => {
    const node = s.nodes.find((n) => n.id === id)
    return node?.type
  })

  const previewRequest = useNodePreviewRequest()

  useEffect(() => {
    if (!previewRequest || previewRequest.nodeId !== id) return

    // 图片/视频/音频节点：需要素材 URL 才打开预览
    if (nodeType === 'image' || nodeType === 'video' || nodeType === 'audio') {
      const hasContent = !!nodeAsset.asset?.url?.trim()
      if (nodeType === 'image' && hasDirectorOp(snapshot)) {
        setDirectorOpen(true)
      } else if (hasContent) {
        if (nodeType === 'image' && hasPanoramaOp(snapshot)) {
          setPanoramaOpen(true)
        } else {
          setPreviewOpen(true)
        }
      }
    } else if (nodeType === 'text') {
      // 文本节点：始终可预览
      setPreviewOpen(true)
    }

    clearNodePreviewRequest(previewRequest.id)
  }, [previewRequest, id, nodeType, nodeAsset.asset?.url, snapshot])

  const handleDoubleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      if (!id) return

      // 双击仅对图片和视频节点打开预览
      if (nodeType !== 'image' && nodeType !== 'video') {
        return
      }

      // 检查是否有可预览的内容
      const hasContent = !!nodeAsset.asset?.url?.trim()

      if (nodeType === 'image' && hasDirectorOp(snapshot)) {
        setDirectorOpen(true)
      } else if (hasContent) {
        if (nodeType === 'image' && hasPanoramaOp(snapshot)) {
          setPanoramaOpen(true)
        } else {
          setPreviewOpen(true)
        }
      }
    },
    [id, nodeType, nodeAsset.asset?.url, snapshot]
  )

  const handleClosePreview = useCallback(() => {
    setPreviewOpen(false)
  }, [])

  const handleClosePanorama = useCallback(() => {
    setPanoramaOpen(false)
  }, [])

  const openPanorama = useCallback(() => {
    setPanoramaOpen(true)
  }, [])

  const handleCloseDirector = useCallback(() => {
    setDirectorOpen(false)
  }, [])

  const openDirector = useCallback(() => {
    setDirectorOpen(true)
  }, [])

  return {
    handleDoubleClick,
    previewOpen,
    handleClosePreview,
    panoramaOpen,
    handleClosePanorama,
    openPanorama,
    directorOpen,
    handleCloseDirector,
    openDirector,
    nodeType,
    assetUrl: nodeAsset.asset?.url,
    title: snapshot?.title,
    material_id: snapshot?.assetRefs?.[0],
  }
}
